import React, { useState, useRef, useEffect } from 'react'
import { Layout, Table, Button, Empty } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import sensors, { sensorsRole } from '../../utils/sensors'
import Filter, { AttendanceFilter } from './Filter'
import Viewer, { ViewerRef } from '../components/ViewerV2'
import CONFIG from '../../constants/config'
import {
  fetchAttendanceDaily,
  fetchAttendanceExcel
} from '../../actions/attendance'
import { fetchPhotoWatermarkExcel } from '../../actions/photoWatermark'
import { weekDay } from '../../utils/utils'
import * as SentryUtils from '../../utils/sentryUtils'
import vip_download_excel_icon from '../../images/vip_download_excel.png'
import { setPaymentVisible } from '../../actions/main'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import parseQueryString from '../../utils/parseQueryString'
import baseColumnsData from '../photoWatermark/lib/baseColumnsData'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout
const { Column } = Table
const DAILY_EXPORT_TYPE = 0
const PERIOD_EXPORT_TYPE = 1
const PHOTO_WATERMARK_EXPORT_TYPE = 2

// 打卡时间类型
interface WorkTime {
  photoTime?: string
  status?: number
  address?: string
  photoURL?: string
  photoSmallURL?: string
  mediaType?: number
}

// 请假时段类型
interface LeaveTime {
  startTime: string
  endTime: string
}

// 时间段类型
interface TimeBucket {
  onWork?: WorkTime
  offWork?: WorkTime
  photoCount?: number
  workSecond?: number
  leave?: LeaveTime[]
}

// 每日考勤数据类型
interface DailyAttendanceData {
  userID: string
  date: string
  status: number
  ruleChunkID?: string
  attendanceTypeFmt?: string
  bucket: TimeBucket[]
  photoCount?: number
  workSecond?: number
  isAskForLeave?: boolean
}

// 规则片段类型
interface RuleChunk {
  ruleChunkID: string
  ruleChunkName: string
}

// 用户类型
interface RecordUser {
  userID: string
  userName: string
}

// 预览照片类型
interface PreviewPhoto {
  largeURL: string
  mediaType: number
  saveName: string
  smallURL: string
  videoURL: string
}

// Excel 任务类型
interface ExcelTask {
  operateTime: string
  exportType: number
  membersNum: number
  startDate: string
  endDate: string
  finished: number
  fileName: string
  url: string
  contentNumber?: number
  requestId: string
}

// Excel 导出结果类型
interface ExcelExportResult {
  exportResult: number
  fileName: string
  exportURL: string
  contentNumber?: number
}

const formatDuration = (seconds: number | undefined) => {
  if (seconds !== undefined) {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.round((seconds % 3600) / 60)
    return `${hours}小时${minutes}分钟`
  }
  return '-'
}

const attendanceStatus = (status: number | undefined) => {
  switch (status) {
    case 0:
      return '正常'
    case 1:
      return <span style={{ color: '#E02020' }}>迟到</span>
    case 2:
      return <span style={{ color: '#E02020' }}>早退</span>
    case 3:
      return <span style={{ color: '#E02020' }}>缺卡</span>
    case 4:
      return '尚未打卡'
    case 5:
      return '无需打卡'
    default:
      return '-'
  }
}

function Daily() {
  const dispatch = useAppDispatch()
  const groupId = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)
  const members = useAppSelector((state) => state.group.members)
  const isVip = useAppSelector((state) => state.group.isVip)
  const myRole = useAppSelector((state) => state.group.myRole)

  const [attendanceData, setAttendanceData] = useState<DailyAttendanceData[]>(
    []
  )
  const [ruleChunks, setRuleChunks] = useState<RuleChunk[]>([])
  const [recordUsers, setRecordUsers] = useState<RecordUser[]>([])
  const [previewPhotos, setPreviewPhotos] = useState<PreviewPhoto[]>([])
  const [pageSize, setPageSize] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  // 初始化时解析 URL 参数，有则用 URL 日期，无则用默认（当月）
  const initFilter = (): AttendanceFilter => {
    const { start_time, end_time } = parseQueryString()
    const unTakePhotoPersonIds = localStorage.getItem('unTakePhotoPersonIds')
    const base: AttendanceFilter = {
      dates: [dayjs().startOf('months'), dayjs().endOf('days')],
      members: [],
      userDepartmentIDs: unTakePhotoPersonIds
        ? JSON.parse(unTakePhotoPersonIds)
        : [],
      departments: []
    }
    if (start_time && end_time) {
      base.dates = [
        dayjs(decodeURIComponent(start_time as string)),
        dayjs(decodeURIComponent(end_time as string))
      ]
    }
    return base
  }

  const [filter, setFilter] = useState<AttendanceFilter>(initFilter)

  const viewerRef = useRef<ViewerRef>(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    localStorage.removeItem('unTakePhotoPersonIds')
    // 初次挂载直接用初始 filter 请求一次
    handleFetchAttendance()
    isInitialMount.current = false
  }, [])

  useEffect(() => {
    // 跳过初次挂载（已在上面的 effect 中请求过）
    if (isInitialMount.current) return
    handleFetchAttendance()
  }, [filter])

  const handleFilterChange = (newFilter: AttendanceFilter) => {
    setFilter(newFilter)
  }

  const handleFetchAttendance = () => {
    const {
      dates,
      members: filterMembers,
      userDepartmentIDs,
      departments
    } = filter
    const isDatesOutOfMaxRange =
      Math.abs(dates[0].diff(dates[1], 'days', true)) > 31
    const isStartTimeAfterEndTime = dates[0].isAfter(dates[1])
    const departmentIDList = departments.map((department) => department.id)
    if (!isDatesOutOfMaxRange && !isStartTimeAfterEndTime && groupId) {
      const params = {
        groupID: groupId,
        userID: userId,
        parameters: {
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          users: filterMembers,
          userIDList: userDepartmentIDs,
          departmentIdList: departmentIDList,
          page: {
            isPagination: false
          }
        }
      }
      dispatch(fetchAttendanceDaily(params))
        .then((data) => {
          setRuleChunks(data.ruleChunks || [])
          setAttendanceData(data.attendanceDatas || [])
          setRecordUsers(data.users || [])
          setCurrentPage(1)
        })
        .catch(SentryUtils.capturePromiseException)
    }
  }

  const handleCalcMemberPickedValue = () => {
    const userNumInDepartments = filter.departments
      .map((department) => department.num)
      .reduce((acc, cur) => acc + cur, 0)
    const total =
      filter.members.length +
      filter.userDepartmentIDs.length +
      userNumInDepartments
    if (
      total === 0 ||
      total === members.length ||
      filter.departments.some((department) => department.id === '0')
    ) {
      return members.length
    } else {
      return total
    }
  }

  const recordExcelTask = ({
    exportType,
    membersNum,
    startDate,
    endDate,
    finished,
    fileName,
    url,
    contentNumber,
    operateTime,
    requestId
  }: Omit<ExcelTask, 'operateTime'> & { operateTime?: string }) => {
    let tasks: ExcelTask[] = []
    const excelTasks = localStorage.getItem('excelTasks')
    if (excelTasks) {
      try {
        tasks = JSON.parse(excelTasks)
      } catch (error) {
        console.log('解析Excel任务列表失败')
      }
    }
    tasks.push({
      operateTime: operateTime || dayjs().format(CONFIG.timeFormat),
      exportType,
      membersNum,
      startDate,
      endDate,
      finished,
      fileName,
      url,
      contentNumber,
      requestId
    })
    localStorage.setItem('excelTasks', JSON.stringify(tasks))
  }

  const triggerAttendanceExcelExport = (
    exportType: number,
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    return dispatch(
      fetchAttendanceExcel({
        groupID: groupId,
        userID: userId,
        parameters: {
          users: filterMembers,
          userIDList: userDepartmentIDs,
          departmentIdList: departmentIDList,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          exportType
        }
      })
    )
      .then((data) => {
        return data
      })
      .catch((error) => {
        SentryUtils.capturePromiseException(error)
        throw error
      })
  }

  const buildPhotoWatermarkDefaultColumns = () => {
    const baseColumns = [
      'userNickname',
      'watermarkName',
      'time',
      'addr',
      'weather',
      'lng',
      'lat',
      'coordinates',
      'altitude'
    ]
    return baseColumns
      .map((key) => {
        const column = baseColumnsData.find((item) => item.dataIndex === key)
        return column ? column.title : null
      })
      .filter(Boolean)
  }

  const encodeExportParams = (params: Record<string, unknown>) => {
    const jsonStr = JSON.stringify(params)
    if (typeof window !== 'undefined' && window.btoa) {
      return window.btoa(unescape(encodeURIComponent(jsonStr)))
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(jsonStr, 'utf-8').toString('base64')
    }
    return jsonStr
  }

  const buildPhotoWatermarkExportPayload = (
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    const startTime = dates[0].clone().startOf('day').format(CONFIG.timeFormat)
    const endTime = dates[1].clone().endOf('day').format(CONFIG.timeFormat)
    const exportParams = {
      times: [
        {
          start: startTime,
          end: endTime
        }
      ],
      watermarks: [],
      keywords: [],
      mediaType: -1,
      users: filterMembers && filterMembers.length ? filterMembers : null,
      userIDList: userDepartmentIDs,
      departmentIDList:
        departmentIDList && departmentIDList.length ? departmentIDList : ['0'],
      existPhotoComment: 0,
      locations: null,
      photoTags: null
    }
    return {
      groupID: groupId,
      exportParams: encodeExportParams(exportParams),
      defaultTitles: buildPhotoWatermarkDefaultColumns(),
      extendTitles: [],
      type: 1,
      formatType: 1
    }
  }

  const triggerPhotoWatermarkExcelExport = (
    dates: [Dayjs, Dayjs],
    filterMembers: string[],
    userDepartmentIDs: string[],
    departmentIDList: string[]
  ) => {
    return dispatch(
      fetchPhotoWatermarkExcel(
        buildPhotoWatermarkExportPayload(
          dates,
          filterMembers,
          userDepartmentIDs,
          departmentIDList
        )
      )
    )
      .then((data) => {
        return data
      })
      .catch((error) => {
        SentryUtils.capturePromiseException(error)
        throw error
      })
  }

  const handleExcel = () => {
    if (!isVip) {
      dispatch(setPaymentVisible(true, 'attendanceDailyChooseDateExcel'))
      return
    }
    const {
      dates,
      members: filterMembers,
      userDepartmentIDs,
      departments
    } = filter
    const departmentIDList = departments.map((department) => department.id)
    const memberCount = handleCalcMemberPickedValue()
    sensors.track('web_workgroup_attandence_page_click_export_excel', {
      pagePath: window.location.pathname,
      start_date: dates[0].format(CONFIG.dateFormat),
      end_date: dates[1].format(CONFIG.dateFormat),
      timePeriod: dates[1].diff(dates[0], 'days') + 1,
      memberNum: memberCount,
      groupID: groupId,
      role: sensorsRole(myRole),
      isGroupVIP: isVip
    })

    // 并行发起三个导出请求，等所有请求结果后按指定顺序写入 localStorage（照片台账 -> 周月统计 -> 每日统计）
    const promises = [
      triggerPhotoWatermarkExcelExport(
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      ),
      triggerAttendanceExcelExport(
        PERIOD_EXPORT_TYPE,
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      ),
      triggerAttendanceExcelExport(
        DAILY_EXPORT_TYPE,
        dates,
        filterMembers,
        userDepartmentIDs,
        departmentIDList
      )
    ]

    Promise.allSettled(promises)
      .then((results) => {
        const [photoRes, periodRes, dailyRes] = results

        const baseTime = dayjs()
        const requestId = `${baseTime.valueOf()}_${Math.random()
          .toString(36)
          .slice(2, 8)}`

        const pushIfFulfilled = (
          res: PromiseSettledResult<ExcelExportResult>,
          makeTask: (data: ExcelExportResult) => Omit<
            ExcelTask,
            'requestId' | 'operateTime'
          > & {
            operateTime?: string
          }
        ) => {
          if (res && res.status === 'fulfilled' && res.value) {
            recordExcelTask({ ...makeTask(res.value), requestId })
          }
        }

        pushIfFulfilled(photoRes, (data) => ({
          exportType: PHOTO_WATERMARK_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].clone().startOf('day').format(CONFIG.timeFormat),
          endDate: dates[1].clone().endOf('day').format(CONFIG.timeFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          contentNumber: data.contentNumber,
          operateTime: baseTime.format(CONFIG.timeFormat)
        }))

        pushIfFulfilled(periodRes, (data) => ({
          exportType: PERIOD_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          operateTime: baseTime
            .clone()
            .add(1, 'seconds')
            .format(CONFIG.timeFormat)
        }))

        pushIfFulfilled(dailyRes, (data) => ({
          exportType: DAILY_EXPORT_TYPE,
          membersNum: memberCount,
          startDate: dates[0].format(CONFIG.dateFormat),
          endDate: dates[1].format(CONFIG.dateFormat),
          finished: data.exportResult,
          fileName: data.fileName,
          url: data.exportURL,
          operateTime: baseTime
            .clone()
            .add(2, 'seconds')
            .format(CONFIG.timeFormat)
        }))

        window.open('/attendance/export-list?fromPlace=attendanceDaily')
      })
      .catch((error) => {
        console.error('导出过程中出现错误:', error)
        window.open('/attendance/export-list?fromPlace=attendanceDaily')
      })
  }

  const handleCheckPhotos = (
    startTime: string,
    endTime: string,
    memberId: string
  ) => {
    sensors.track('web_workgroup_attandence_page_click', {
      clickItem: 'photoNum',
      pagePath: window.location.pathname
    })
    window.open(
      `/photo?start_time=${encodeURIComponent(
        startTime
      )}&end_time=${encodeURIComponent(endTime)}&member_id=${encodeURIComponent(
        memberId
      )}`
    )
  }

  const handleCheckTimePhoto = (
    record: DailyAttendanceData,
    bucketIndex: number,
    workType: 'onWork' | 'offWork'
  ) => {
    const time = record.bucket[bucketIndex][workType] as WorkTime
    const author =
      recordUsers.find((user) => user.userID === record.userID)?.userName || ''

    const media: PreviewPhoto = {
      largeURL: time.photoURL || '',
      mediaType: time.mediaType || 0,
      saveName: `${author} ${record.date} ${time.photoTime}${
        time.mediaType === 0 ? '.jpg' : '.mp4'
      }`,
      smallURL: time.photoSmallURL || '',
      videoURL: time.photoURL || ''
    }
    sensors.track('web_workgroup_attandence_page_click', {
      clickItem: 'checkTime',
      pagePath: window.location.pathname
    })
    setPreviewPhotos([media])
    setTimeout(() => viewerRef.current?.view(0), 0)
  }

  const renderAttendanceRule = (record: DailyAttendanceData) => {
    const currentRuleObj = ruleChunks.filter(
      (item) => item.ruleChunkID === record.ruleChunkID
    )

    if (currentRuleObj.length) {
      return currentRuleObj[0]
    }

    return {
      ruleChunkName: '默认考勤规则',
      ruleChunkID: '-2'
    }
  }

  const notNeedAttendance = (record: DailyAttendanceData) => {
    if (record.bucket && record.bucket.length) {
      for (const item of record.bucket) {
        if (item.offWork?.status === 5) {
          return true
        }
      }
    }
    return false
  }

  const renderTable = () => {
    if (attendanceData.length > 0) {
      const dataSource = attendanceData
        ? attendanceData
            .slice(pageSize * (currentPage - 1), pageSize * currentPage)
            .map((row, index) => {
              return {
                key: index,
                ...row
              }
            })
        : []

      const timesColumns = () => {
        const columns: React.ReactElement[] = []
        for (let index = 0; index < 10; index++) {
          columns.push(
            <Column<DailyAttendanceData>
              key={`${index}1`}
              title={`上班打卡时间${index + 1}`}
              width={130}
              render={(_, record) => {
                const text = record.bucket?.[index]?.onWork?.photoTime
                if (record.status === 2) {
                  return '-'
                } else if (text) {
                  return (
                    <a
                      onClick={() =>
                        handleCheckTimePhoto(record, index, 'onWork')
                      }
                    >
                      {text}
                    </a>
                  )
                } else {
                  return '-'
                }
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}2`}
              title={`上班打卡地点${index + 1}`}
              width={140}
              render={(_, record) => {
                const text = record.bucket?.[index]?.onWork?.address
                return (
                  <div
                    style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
                  >
                    {record.status === 2 && '-'}
                    {record.status !== 2 && (text || '-')}
                  </div>
                )
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}3`}
              title={`上班打卡结果${index + 1}`}
              width={130}
              render={(_, record) => {
                const text = record.bucket?.[index]?.onWork?.status
                if (record.status === 2) {
                  return '-'
                } else {
                  return attendanceStatus(text) || '-'
                }
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}4`}
              title={`下班打卡时间${index + 1}`}
              width={130}
              render={(_, record) => {
                const text = record.bucket?.[index]?.offWork?.photoTime
                if (
                  (record.bucket.length > index &&
                    record.bucket[index].offWork?.status === 5) ||
                  record.status === 2
                ) {
                  return '-'
                } else if (text) {
                  return (
                    <a
                      onClick={() =>
                        handleCheckTimePhoto(record, index, 'offWork')
                      }
                    >
                      {text}
                    </a>
                  )
                } else {
                  return '-'
                }
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}5`}
              title={`下班打卡地点${index + 1}`}
              width={140}
              render={(_, record) => {
                const text = record.bucket?.[index]?.offWork?.address
                return (
                  <div
                    style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
                  >
                    {record.bucket.length <= index && '-'}
                    {record.bucket.length > index &&
                      record.bucket[index].offWork?.status === 5 &&
                      '-'}
                    {record.bucket.length > index &&
                      record.bucket[index].offWork?.status !== 5 &&
                      record.status === 2 &&
                      '-'}
                    {record.bucket.length > index &&
                      record.bucket[index].offWork?.status !== 5 &&
                      record.status !== 2 &&
                      (text || '-')}
                  </div>
                )
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}6`}
              title={`下班打卡结果${index + 1}`}
              width={130}
              render={(_, record) => {
                const text = record.bucket?.[index]?.offWork?.status
                if (
                  (record.bucket.length > index &&
                    record.bucket[index].offWork?.status === 5) ||
                  record.status === 2
                ) {
                  return '-'
                } else {
                  return attendanceStatus(text) || '-'
                }
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}7`}
              title={`时段${index + 1}-拍照张数`}
              width={140}
              render={(_, record) => {
                const text = record.bucket?.[index]?.photoCount
                if (record.status === 2) {
                  return '-'
                } else {
                  return text || '-'
                }
              }}
            />,
            <Column<DailyAttendanceData>
              key={`${index}8`}
              title={`时段${index + 1}-工作时长`}
              width={140}
              render={(_, record) => {
                const text = record.bucket?.[index]?.workSecond
                if (
                  (record.bucket.length > index &&
                    record.bucket[index].offWork?.status === 5) ||
                  record.status === 2
                ) {
                  return '-'
                } else {
                  return formatDuration(text) || '-'
                }
              }}
            />
          )
        }
        return columns
      }

      return (
        <div className="table-wrapper">
          {attendanceData && (
            <Table
              bordered
              scroll={{
                x: 1310,
                y: window.innerHeight - 420,
                scrollToFirstRowOnChange: true
              }}
              dataSource={dataSource}
              pagination={{
                total: attendanceData.length,
                showTotal: (total) => `共 ${total} 条`,
                showSizeChanger: true,
                pageSizeOptions: ['20', '30', '50'],
                pageSize: pageSize,
                onShowSizeChange: (current, size) => {
                  setPageSize(size)
                  setCurrentPage(current)
                },
                current: currentPage,
                onChange: (current, size) => {
                  setPageSize(size)
                  setCurrentPage(current)
                }
              }}
            >
              <Column<DailyAttendanceData>
                title="姓名"
                width={130}
                fixed="left"
                dataIndex="userID"
                render={(text) => {
                  const curUser = recordUsers.find(
                    (user) => user.userID === text
                  )
                  return (
                    <div
                      style={{
                        wordWrap: 'break-word',
                        wordBreak: 'break-word'
                      }}
                    >
                      {curUser ? curUser.userName : ''}
                    </div>
                  )
                }}
              />
              <Column<DailyAttendanceData>
                title="考勤规则"
                width={195}
                fixed="left"
                dataIndex="ruleChunkID"
                render={(_, record) => (
                  <div
                    style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
                  >
                    {renderAttendanceRule(record).ruleChunkName}
                  </div>
                )}
              />
              <Column<DailyAttendanceData>
                title="班次"
                width={195}
                fixed="left"
                dataIndex="attendanceTypeFmt"
                render={(text, record) => (
                  <div
                    style={{ wordWrap: 'break-word', wordBreak: 'break-word' }}
                  >
                    {record.status === 2 && '休息日'}
                    {record.status !== 2 && (text || '-')}
                  </div>
                )}
              />
              <Column<DailyAttendanceData>
                title="日期"
                width={120}
                fixed="left"
                dataIndex="date"
              />
              <Column<DailyAttendanceData>
                title="星期"
                width={80}
                render={(_, record) => weekDay(dayjs(record.date))}
              />
              <Column<DailyAttendanceData>
                title="总拍照张数"
                width={110}
                dataIndex="photoCount"
                render={(text, record) => {
                  if (
                    record.status === 2 ||
                    renderAttendanceRule(record).ruleChunkID === '-1'
                  ) {
                    return '-'
                  } else if (text) {
                    return (
                      <a
                        onClick={() =>
                          handleCheckPhotos(
                            `${record.date} 00:00`,
                            `${record.date} 23:59`,
                            record.userID
                          )
                        }
                      >
                        {text}
                      </a>
                    )
                  } else {
                    return <span>{text}</span>
                  }
                }}
              />
              <Column<DailyAttendanceData>
                title="总工作时长（小时）"
                width={160}
                dataIndex="workSecond"
                render={(text, record) => {
                  if (
                    notNeedAttendance(record) ||
                    record.status === 2 ||
                    renderAttendanceRule(record).ruleChunkID === '-1'
                  ) {
                    return '-'
                  } else {
                    return (record.photoCount || 0) > 0 ? (
                      formatDuration(text)
                    ) : (
                      <span style={{ color: 'rgb(224, 32, 32)' }}>未出勤</span>
                    )
                  }
                }}
              />
              <Column<DailyAttendanceData>
                title="当日是否请假"
                width={160}
                dataIndex="isAskForLeave"
                render={(isAskForLeave) => (isAskForLeave ? '请假' : '-')}
              />
              <Column<DailyAttendanceData>
                title="请假时段"
                width={160}
                key="leave_time"
                render={(_, record) => {
                  if (!record.isAskForLeave) {
                    return '-'
                  }

                  return record.bucket
                    .map((item) => item.leave || [])
                    .reduce((pre, cur) => [...pre, ...cur], [])
                    .map(({ startTime, endTime }, index) => (
                      <div key={index}>
                        {startTime} - {endTime}
                      </div>
                    ))
                }}
              />
              {timesColumns()}
            </Table>
          )}
          <Viewer
            ref={viewerRef}
            canDelete={false}
            canRectify={false}
            canAiSecurity={false}
            photos={previewPhotos}
            isHideFoldersInfo={true}
          />
        </div>
      )
    } else {
      return <Empty style={{ marginTop: '200px' }} />
    }
  }

  return (
    <Layout className="Attendance">
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title="每日统计"
        extra={[
          <Button
            key="0"
            type="link"
            onClick={() =>
              window.open('/attendance/export-list?fromPlace=attendanceDaily')
            }
          >
            下载记录
          </Button>,
          <Button
            key="1"
            type="primary"
            disabled={!attendanceData}
            onClick={handleExcel}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              下载表格Excel
              <img
                src={vip_download_excel_icon}
                alt=""
                style={{
                  marginLeft: '4px',
                  width: 20,
                  height: 20
                }}
              />
            </div>
          </Button>
        ]}
      />
      <Content className="content">
        <Filter filter={filter} onChange={handleFilterChange} />
        {renderTable()}
      </Content>
    </Layout>
  )
}

export default Daily
